/* DisputeDrawer — raise / manage a courier-invoice dispute (PS.openDispute port).
   Stepper · invoice detail list · reason+status+CN+owner+note form · audit
   timeline · Raise/Save + Reject. Closing & finance handoff (FINAL) is admin-only;
   this replica runs as admin so it is enabled. */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import CheckIcon from '@mui/icons-material/CheckOutlined'
import CloseIcon from '@mui/icons-material/CloseOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {SideDrawer, ConfirmDialog, SearchSelect, inr, fmtDateTime} from '../components'
import {
  RECON,
  FINAL,
  STEPPER_STATES,
  INV_REASONS,
  ReconBadge,
  VertChip,
  shortStatus,
  isClosed,
  needsCN,
  commitDispute,
  ME,
} from './reconShared'

const OWNERS = ['analyst@prozo.com', 'recon@prozo.com', 'ops@prozo.com']

function Stepper({status}) {
  const curIdx = STEPPER_STATES.indexOf(status)
  const rejected = status === 'Rejected'
  return (
    <div className='stepper' style={{margin: '0 0 16px'}}>
      {STEPPER_STATES.map((s, idx) => {
        const cls = rejected
          ? ''
          : curIdx < 0
            ? ''
            : idx < curIdx
              ? 'done'
              : idx === curIdx
                ? 'active'
                : ''
        return (
          <div className={'step ' + cls} key={s}>
            <span className='num'>{idx < curIdx ? '✓' : idx + 1}</span>
            <span className='lbl' style={{whiteSpace: 'normal', lineHeight: 1.25}}>
              {shortStatus(s)}
            </span>
            {idx < 3 ? <span className='bar' /> : null}
          </div>
        )
      })}
    </div>
  )
}
Stepper.propTypes = {status: PropTypes.string}

function Dl({k, children}) {
  return (
    <>
      <dt>{k}</dt>
      <dd>{children}</dd>
    </>
  )
}
Dl.propTypes = {k: PropTypes.string, children: PropTypes.node}

export default function DisputeDrawer({invoice, raise, isAdmin = true, onClose, onSaved, toast}) {
  const i = invoice
  const open = !!i
  const raising = !!(i && (raise || i.reconStatus === 'No Dispute'))

  const [reason, setReason] = useState('')
  const [status, setStatus] = useState('')
  const [cn, setCn] = useState('')
  const [owner, setOwner] = useState(OWNERS[0])
  const [note, setNote] = useState('')
  const [err, setErr] = useState({})
  const [confirmReject, setConfirmReject] = useState(false)

  // sync form whenever a new invoice opens
  const lastId = React.useRef(null)
  React.useEffect(() => {
    if (!i) return
    if (lastId.current === i.id) return
    lastId.current = i.id
    const initStatus = i.reconStatus !== 'No Dispute' ? i.reconStatus : ''
    setReason(i.reason || '')
    setStatus(initStatus)
    setCn(i.cnAmount || '')
    setOwner(i.owner || OWNERS[0])
    setNote('')
    setErr({})
  }, [i])
  React.useEffect(() => {
    if (!open) lastId.current = null
  }, [open])

  if (!i) return null

  const options = RECON.filter((r) => r.k !== 'No Dispute').map((r) => ({
    k: r.k,
    disabled: r.k === FINAL && !isAdmin,
  }))
  const history = (i.history || []).slice().reverse()

  const save = () => {
    const e = {}
    if (!reason) e.reason = true
    if (!status) e.status = true
    if (status === FINAL && !isAdmin) {
      toast('Only ProShip Admin can close & share with finance.', 'error')
      return
    }
    if (status === FINAL && !(cn !== '' && +cn >= 0)) {
      e.cn = true
      toast('Enter the final CN amount before closing.', 'error')
    }
    if (Object.keys(e).length) {
      setErr(e)
      return
    }
    commitDispute(i, {
      reason,
      status,
      cn: cn === '' ? i.cnAmount || 0 : +cn,
      owner,
      note: note.trim(),
    })
    onClose()
    toast('Invoice ' + i.id + ' → ' + status + '.', status === FINAL ? 'success' : 'info')
    onSaved()
  }

  const doReject = (rsn) => {
    commitDispute(i, {reason: reason || i.reason, status: 'Rejected', cn: 0, owner, note: rsn})
    setConfirmReject(false)
    onClose()
    toast('Dispute on ' + i.id + ' rejected.', 'success')
    onSaved()
  }

  const footer = (
    <>
      <button className='btn btn-ghost' onClick={onClose}>
        Cancel
      </button>
      {isClosed(i) ? null : (
        <button className='btn btn-danger' onClick={() => setConfirmReject(true)}>
          <CloseIcon style={{width: 16, height: 16}} />
          Reject
        </button>
      )}
      <button className='btn btn-primary' onClick={save}>
        <CheckIcon style={{width: 16, height: 16}} />
        {raising ? 'Raise Dispute' : 'Save'}
      </button>
    </>
  )

  return (
    <>
      <SideDrawer
        open={open}
        onClose={onClose}
        width={560}
        title={(raising ? 'Raise Dispute · ' : 'Manage Dispute · ') + i.id}
        footer={footer}
      >
        {!raising ? (
          i.reconStatus === 'Rejected' ? (
            <div style={{marginBottom: 16}}>
              <ReconBadge status={i.reconStatus} />
            </div>
          ) : (
            <Stepper status={i.reconStatus} />
          )
        ) : null}

        <dl className='dl' style={{marginBottom: 14}}>
          <Dl k='Invoice'>
            <span className='mono'>{i.id}</span>
          </Dl>
          <Dl k='Partner'>
            {i.partner} <VertChip vertical={i.vertical} />
          </Dl>
          <Dl k='Period'>{i.period}</Dl>
          <Dl k='Billed'>{inr(i.billedAmount)}</Dl>
          <Dl k='Expected (rate card)'>{inr(i.expectedAmount)}</Dl>
          <Dl k='Variance / disputed'>
            <b style={{color: 'var(--error)'}}>{inr(i.variance)}</b>
          </Dl>
        </dl>

        <div className={'field' + (err.reason ? ' invalid' : '')}>
          <label>
            Reason code <span className='req'>*</span>
          </label>
          <SearchSelect
            value={reason}
            onChange={(v) => {
              setReason(v)
              setErr((p) => ({...p, reason: false}))
            }}
            options={INV_REASONS}
            placeholder='Search…'
            invalid={!!err.reason}
          />
          <div className='vmsg'>Pick a reason.</div>
        </div>

        <div className={'field' + (err.status ? ' invalid' : '')}>
          <label>
            Status / remark <span className='req'>*</span>
          </label>
          <SearchSelect
            value={status}
            onChange={(v) => {
              setStatus(v)
              setErr((p) => ({...p, status: false}))
            }}
            options={options.map((o) => ({
              value: o.k,
              label: o.k + (o.disabled ? '  (admin only)' : ''),
            }))}
            placeholder='Search…'
            invalid={!!err.status}
          />
          <div className='help'>
            <InfoOutlinedIcon />
            {isAdmin
              ? 'You can move this to “Closed & Shared with Finance”.'
              : 'Only ProShip Admin can select “Closed & Shared with Finance”.'}
          </div>
        </div>

        <div className={'field' + (err.cn ? ' invalid' : '')}>
          <label>
            Final CN amount{' '}
            {needsCN(status) ? (
              <span className='req'>*</span>
            ) : (
              <span className='opt'>(when CN received)</span>
            )}
          </label>
          <input
            className={'input' + (err.cn ? ' err' : '')}
            type='number'
            min='0'
            value={cn}
            placeholder={'Credit note (≤ disputed ' + inr(i.disputedAmount) + ')'}
            onChange={(e) => {
              setCn(e.target.value)
              setErr((p) => ({...p, cn: false}))
            }}
          />
          <div className='help'>
            <InfoOutlinedIcon />
            The credit note value the courier has agreed/issued. Recovery rate uses this.
          </div>
          <div className='vmsg'>Enter the final CN amount to close.</div>
        </div>

        <div className='field'>
          <label>Owner</label>
          <SearchSelect value={owner} onChange={(v) => setOwner(v)} options={OWNERS} placeholder='Search…' />
        </div>

        <div className='field'>
          <label>
            Remark / note <span className='opt'>(optional)</span>
          </label>
          <textarea
            className='input'
            rows={2}
            value={note}
            placeholder='e.g. Shared POD; courier agreed to Zone 4 reprice. CN ref CN-88123.'
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <div className='seclabel' style={{margin: '8px 0 12px'}}>
          Audit trail
        </div>
        <div className='timeline'>
          {history.length ? (
            history.map((h, idx) => (
              <div className={'tl-item ' + (h.status === 'Rejected' ? 'red' : 'green')} key={idx}>
                <div className='tl-card'>
                  <div className='row between center'>
                    <b>{h.status}</b>
                    <span className='muted' style={{fontSize: 12}}>
                      {fmtDateTime(h.at)}
                    </span>
                  </div>
                  <div className='tl-meta'>
                    by {h.by || ME}
                    {h.reason ? ' · ' + h.reason : ''}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className='muted' style={{fontSize: 13}}>
              No transitions yet.
            </div>
          )}
        </div>
      </SideDrawer>

      <ConfirmDialog
        open={confirmReject}
        onClose={() => setConfirmReject(false)}
        title={'Reject dispute on ' + i.id + '?'}
        body='Courier rejected the claim — no credit note. A reason is required.'
        needReason
        okLabel='Reject'
        danger
        onOk={doReject}
      />
    </>
  )
}

DisputeDrawer.propTypes = {
  invoice: PropTypes.object,
  raise: PropTypes.bool,
  isAdmin: PropTypes.bool,
  onClose: PropTypes.func,
  onSaved: PropTypes.func,
  toast: PropTypes.func,
}
